/**
 * 场景关联技能的纯匹配逻辑 —— 不碰 IPC、不碰 window，可直接被 node --test import。
 * 有副作用的探测/安装在 scenarioSkills.ts。
 */
import type { Skill } from '../types/skill';
import type { ScenarioSkill } from './scenario';

/**
 * 单个场景技能在本地的状态。
 * - ready       已装且已启用，可以进 prompt
 * - missing     本地没有，需要从技能市场安装
 * - disabled    本地有但被用户在技能管理里关掉了 —— 需要用户确认才启用，不静默复活
 * - unavailable 平台侧已下架且本地也没装 —— 装不了（下载接口 404），只能如实告知
 * - installing / failed 仅存在于装配过程中
 */
export type ScenarioSkillState = 'ready' | 'missing' | 'disabled' | 'unavailable' | 'installing' | 'failed';

/** 平台侧已下架。老版本 platform 不下发 status，undefined 按启用处理。 */
function isDelistedOnPlatform(link: ScenarioSkill): boolean {
  return link.status !== undefined && link.status !== 1;
}

export interface ScenarioSkillEntry {
  /** 管理端下发的关联信息（skill_name 即 slug）。 */
  link: ScenarioSkill;
  /** 匹配到的本地技能；未安装时为 null。 */
  skill: Skill | null;
  state: ScenarioSkillState;
  /** 安装失败原因，供 UI 展示。 */
  error?: string;
}

export interface ScenarioSkillResolution {
  entries: ScenarioSkillEntry[];
  /** 已装且已启用，可直接进 prompt。 */
  ready: Skill[];
  /** 本地缺失且平台可下载，安装即可用。 */
  missing: ScenarioSkill[];
  /** 本地已装但被用户禁用，需用户确认后启用。 */
  disabled: Skill[];
  /** 平台已下架且本地没装：装不了，只能告知用户这条关联失效。 */
  unavailable: ScenarioSkill[];
}

/** 场景目录以 skill_name 作为稳定 ID；只清理边界空白，保留大小写及字符，禁止展示名碰撞。 */
export function normalizeSkillKey(value: string): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 场景关联技能列表的指纹 —— 缓存键与 UI 重算的**唯一**依据。
 *
 * 三个字段都得进去：
 *  - slug：换了关联哪些技能
 *  - version：技能内容更新
 *  - status：上架/下架切换。这个最容易漏——技能被下架时 slug 和 version 通常一个字都不变，
 *    只看前两者的话客户端会一直吃旧结果：明明已下架，界面还显示"未安装"并让用户去点安装，
 *    点了必然 404。反过来重新上架同理，会一直卡在"已下架"。
 */
export function scenarioSkillsFingerprint(links: ScenarioSkill[] | undefined | null): string {
  if (!links?.length) return '';
  return links
    .map(l => `${normalizeSkillKey(l.skill_name)}@${l.version ?? ''}#${l.status ?? ''}`)
    .sort()
    .join('|');
}

/**
 * 把场景关联项与本地技能列表对上号。
 * 与在线安装预检一致，只按技能 ID 匹配；展示名称不能证明已安装。
 */
export function matchScenarioSkills(
  links: ScenarioSkill[],
  localSkills: Skill[],
): ScenarioSkillResolution {
  const byKey = new Map<string, Skill>();
  for (const skill of localSkills || []) {
    const key = normalizeSkillKey(skill.id);
    if (key && !byKey.has(key)) byKey.set(key, skill);
  }

  const entries: ScenarioSkillEntry[] = [];
  const seen = new Set<string>();
  for (const link of links || []) {
    const key = normalizeSkillKey(link.skill_name);
    // 同一场景重复关联同一技能时只保留一条，避免 prompt 里出现重复块
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const skill = byKey.get(key) || null;
    // 已装的技能不受平台下架影响——下架只挡住"新安装"，本地那份照常能用。
    // 只有"平台下架 + 本地没装"才是真的无解。
    const state: ScenarioSkillState = skill
      ? (skill.enabled ? 'ready' : 'disabled')
      : (isDelistedOnPlatform(link) ? 'unavailable' : 'missing');
    entries.push({ link, skill, state });
  }

  return {
    entries,
    ready: entries.filter(e => e.state === 'ready').map(e => e.skill!),
    missing: entries.filter(e => e.state === 'missing').map(e => e.link),
    disabled: entries.filter(e => e.state === 'disabled').map(e => e.skill!),
    unavailable: entries.filter(e => e.state === 'unavailable').map(e => e.link),
  };
}

/**
 * 有上限的并发执行。技能包动辄几 MB，同时拉太多既拖慢又容易触发平台限流。
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      for (;;) {
        const index = cursor++;
        if (index >= items.length) return;
        results[index] = await worker(items[index], index);
      }
    }),
  );
  return results;
}
